(() => {
  const ELEMENT_NAME = 'luma-cod-popup';
  const OFFICIAL_PRICES = Object.freeze({ 1: 200, 2: 300, 3: 450 });
  const ORDER_ID_PATTERN = /^NLUMA-\d{8}-[A-Z0-9]{6,24}$/;
  const REQUEST_TIMEOUT_MS = 20000;
  const SWIPE_COMPLETION_THRESHOLD = 0.9;

  class LumaCodPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.dialog = this.querySelector('[data-luma-cod-dialog]');
      this.form = this.querySelector('[data-luma-cod-form]');
      this.formView = this.querySelector('[data-luma-cod-view="form"]');
      this.successView = this.querySelector('[data-luma-cod-view="success"]');
      this.productsContainer = this.querySelector('[data-luma-cod-products]');
      this.offerOutput = this.querySelector('[data-luma-cod-offer]');
      this.quantityOutput = this.querySelector('[data-luma-cod-quantity]');
      this.totalOutput = this.querySelector('[data-luma-cod-total]');
      this.formStatus = this.querySelector('[data-luma-cod-form-status]');
      this.nativeSubmitButton = this.querySelector('[data-luma-cod-submit]');
      this.swipe = this.querySelector('[data-luma-cod-swipe]');
      this.swipeHandle = this.querySelector('[data-luma-cod-swipe-handle]');
      this.swipeLabel = this.querySelector('[data-luma-cod-swipe-label]');
      this.swipeGuide = this.querySelector('[data-luma-cod-swipe-guide]');
      this.spinner = this.querySelector('[data-luma-cod-spinner]');
      this.orderIdOutput = this.querySelector('[data-luma-cod-order-id]');
      this.testNote = this.querySelector('[data-luma-cod-test-note]');
      this.testMode = this.dataset.testMode === 'true';
      this.orderContext = null;
      this.idempotencyKey = '';
      this.packFingerprint = '';
      this.submitting = false;
      this.swipeComplete = false;
      this.swipeProgress = 0;
      this.swipePointerId = null;
      this.swipeStartX = 0;
      this.swipeStartProgress = 0;
      this.requestController = null;
      this.returnFocus = null;
      this.swipeInteracted = false;
      this.swipeErrorTimer = null;

      if (this.testNote) this.testNote.hidden = !this.testMode;

      this.onPackReady = this.onPackReady.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onSwipePointerDown = this.onSwipePointerDown.bind(this);
      this.onSwipePointerMove = this.onSwipePointerMove.bind(this);
      this.onSwipePointerEnd = this.onSwipePointerEnd.bind(this);
      this.onSwipePointerCancel = this.onSwipePointerCancel.bind(this);
      this.onResize = this.onResize.bind(this);

      window.addEventListener(
        this.dataset.readyEvent || 'luma:pack-ready',
        this.onPackReady,
      );
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('input', this.onInput);
      this.form?.addEventListener('submit', this.onSubmit);
      this.swipeHandle?.addEventListener(
        'pointerdown',
        this.onSwipePointerDown,
      );
      this.swipeHandle?.addEventListener(
        'pointermove',
        this.onSwipePointerMove,
      );
      this.swipeHandle?.addEventListener('pointerup', this.onSwipePointerEnd);
      this.swipeHandle?.addEventListener(
        'pointercancel',
        this.onSwipePointerCancel,
      );
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener(
        this.dataset.readyEvent || 'luma:pack-ready',
        this.onPackReady,
      );
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.swipeHandle?.removeEventListener(
        'pointerdown',
        this.onSwipePointerDown,
      );
      this.swipeHandle?.removeEventListener(
        'pointermove',
        this.onSwipePointerMove,
      );
      this.swipeHandle?.removeEventListener(
        'pointerup',
        this.onSwipePointerEnd,
      );
      this.swipeHandle?.removeEventListener(
        'pointercancel',
        this.onSwipePointerCancel,
      );
      window.removeEventListener('resize', this.onResize);
      if (this.swipeErrorTimer) window.clearTimeout(this.swipeErrorTimer);
      this.requestController?.abort();
    }

    safeQuantity(value) {
      const quantity = Number(value);
      return [1, 2, 3].includes(quantity) ? quantity : null;
    }

    safeText(value, maxLength) {
      return typeof value === 'string'
        ? value.replace(/\s+/g, ' ').trim().slice(0, maxLength)
        : '';
    }

    normalizeProduct(value) {
      if (!value || typeof value !== 'object') return null;
      const productId = this.safeText(value.productId, 100);
      const variantId = this.safeText(value.variantId, 100);
      const title = this.safeText(value.title, 240);
      const imageUrl = this.safeUrl(value.imageUrl);
      if (!productId || !variantId || !title) return null;
      return {
        productId,
        variantId,
        title,
        handle: this.safeText(value.handle, 180),
        imageUrl,
        productUrl: this.safeUrl(value.productUrl),
      };
    }

    safeUrl(value) {
      if (typeof value !== 'string' || !value) return '';
      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    }

    normalizePack(detail) {
      if (!detail || typeof detail !== 'object') return null;
      const offerQuantity = this.safeQuantity(detail.offerQuantity);
      if (!offerQuantity || !Array.isArray(detail.selectedProducts)) return null;

      const selectedProducts = detail.selectedProducts
        .map((product) => this.normalizeProduct(product))
        .filter(Boolean);
      const productIds = new Set(
        selectedProducts.map((product) => product.productId),
      );
      const variantIdsPresent = selectedProducts.every(
        (product) => product.variantId,
      );

      if (
        selectedProducts.length !== offerQuantity ||
        productIds.size !== selectedProducts.length ||
        !variantIdsPresent
      ) {
        return null;
      }

      return {
        offerQuantity,
        totalPrice: OFFICIAL_PRICES[offerQuantity],
        selectedProducts,
      };
    }

    onPackReady(event) {
      const pack = this.normalizePack(event.detail);
      if (!pack) return;

      const fingerprint = [
        pack.offerQuantity,
        ...pack.selectedProducts.map(
          (product) => `${product.productId}:${product.variantId}`,
        ),
      ].join('|');
      if (fingerprint !== this.packFingerprint) {
        this.packFingerprint = fingerprint;
        this.idempotencyKey = this.createIdempotencyKey();
      }

      this.orderContext = pack;
      this.renderSummary();
      this.showFormView();
      this.open();
    }

    createIdempotencyKey() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      const bytes = new Uint8Array(16);
      window.crypto?.getRandomValues?.(bytes);
      const random = Array.from(bytes, (value) =>
        value.toString(16).padStart(2, '0'),
      ).join('');
      return `luma-${Date.now().toString(36)}-${random || Math.random().toString(36).slice(2)}`;
    }

    renderSummary() {
      if (!this.orderContext) return;
      const { offerQuantity, totalPrice, selectedProducts } = this.orderContext;

      if (this.offerOutput) {
        this.offerOutput.textContent =
          offerQuantity === 1
            ? 'عطر واحد'
            : offerQuantity === 2
              ? 'جوج عطور'
              : '3 عطور';
      }
      if (this.quantityOutput) {
        this.quantityOutput.textContent = String(offerQuantity);
      }
      if (this.totalOutput) {
        this.totalOutput.textContent = `${totalPrice} درهم`;
      }
      if (!this.productsContainer) return;

      this.productsContainer.replaceChildren();
      selectedProducts.forEach((product) => {
        const item = document.createElement('article');
        item.className = 'luma-cod__product';

        const imageWrap = document.createElement('div');
        imageWrap.className = 'luma-cod__product-image';
        if (product.imageUrl) {
          const image = document.createElement('img');
          image.src = product.imageUrl;
          image.alt = '';
          image.width = 160;
          image.height = 160;
          image.loading = 'lazy';
          image.decoding = 'async';
          imageWrap.append(image);
        }

        const title = document.createElement('strong');
        title.textContent = product.title;
        item.append(imageWrap, title);
        this.productsContainer.append(item);
      });
    }

    open() {
      this.returnFocus =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      this.hidden = false;
      document.body.classList.add('luma-cod-open');
      window.requestAnimationFrame(() => {
        const firstField = this.querySelector('[data-luma-cod-input]');
        (firstField instanceof HTMLElement ? firstField : this.dialog)?.focus();
      });
    }

    close() {
      this.requestController?.abort();
      this.setSubmitting(false);
      this.resetSwipe(true);
      this.hidden = true;
      document.body.classList.remove('luma-cod-open');
      this.returnFocus?.focus({ preventScroll: true });
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-luma-cod-close]')) {
        this.close();
        return;
      }
      if (
        target.matches('[data-luma-cod-backdrop]') &&
        !this.submitting
      ) {
        this.close();
      }
    }

    onKeydown(event) {
      if (event.target === this.swipeHandle && this.onSwipeKeydown(event)) {
        return;
      }
      if (event.key === 'Escape' && !this.submitting) {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter(
        (element) =>
          element instanceof HTMLElement &&
          element.offsetParent !== null &&
          !element.closest('[hidden]'),
      );
      if (!focusable.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onInput(event) {
      const input = event.target instanceof HTMLElement ? event.target : null;
      const name =
        input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement
          ? input.name
          : '';
      if (name) this.setFieldError(name, '');
      this.showFormStatus('');
    }

    swipeMaxTravel() {
      if (!this.swipe || !this.swipeHandle) return 0;
      const trackWidth =
        this.swipe.getBoundingClientRect?.().width || this.swipe.clientWidth || 0;
      const handleWidth =
        this.swipeHandle.getBoundingClientRect?.().width ||
        this.swipeHandle.offsetWidth ||
        0;
      return Math.max(0, trackWidth - handleWidth - 8);
    }

    setSwipeProgress(value) {
      const progress = Math.max(0, Math.min(1, Number(value) || 0));
      const travel = this.swipeMaxTravel();
      this.swipeProgress = progress;
      this.swipe?.style.setProperty(
        '--luma-swipe-offset',
        `${travel * progress}px`,
      );
      this.swipe?.style.setProperty(
        '--luma-swipe-progress',
        String(progress),
      );
      this.swipe?.classList.toggle('is-complete', progress >= 1);
      if (this.swipeHandle) {
        const percentage = Math.round(progress * 100);
        this.swipeHandle.setAttribute('aria-valuenow', String(percentage));
        this.swipeHandle.setAttribute(
          'aria-label',
          progress >= 1
            ? 'تم تأكيد السحب'
            : `اسحب من اليسار إلى اليمين لتأكيد الطلب — ${percentage}%`,
        );
      }
    }

    setSwipeLabel(message) {
      if (this.swipeLabel) this.swipeLabel.textContent = message;
    }

    markSwipeInteraction() {
      this.swipeInteracted = true;
      this.swipe?.classList.add('has-interacted');
      this.swipe?.classList.remove('is-guide-active');
    }

    startSwipeGuide() {
      if (!this.swipe || this.swipeInteracted) return;
      this.swipe.classList.remove('is-guide-active');
      window.requestAnimationFrame(() => {
        this.swipe?.classList.add('is-guide-active');
      });
    }

    onResize() {
      if (!this.hidden) this.setSwipeProgress(this.swipeProgress);
    }

    resetSwipe(force = false) {
      if (this.submitting && !force) return;
      this.swipeComplete = false;
      this.swipePointerId = null;
      this.swipe?.classList.remove(
        'is-dragging',
        'is-complete',
        'is-success',
      );
      this.setSwipeProgress(0);
      if (!this.swipe?.classList.contains('is-error')) {
        this.setSwipeLabel('اسحب لتأكيد الطلب');
      }
    }

    completeSwipe() {
      if (this.submitting || this.swipeComplete || !this.form) return;
      this.swipeComplete = true;
      this.setSwipeProgress(1);
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.nativeSubmitButton || undefined);
      } else {
        this.nativeSubmitButton?.click();
      }
    }

    onSwipePointerDown(event) {
      if (
        this.submitting ||
        this.swipeComplete ||
        event.isPrimary === false ||
        (event.pointerType === 'mouse' && event.button !== 0) ||
        this.swipeMaxTravel() <= 0
      ) {
        return;
      }
      event.preventDefault();
      this.markSwipeInteraction();
      this.swipe?.classList.remove('is-error');
      this.setSwipeLabel('كمل السحب للتأكيد');
      this.swipePointerId = event.pointerId;
      this.swipeStartX = event.clientX;
      this.swipeStartProgress = this.swipeProgress;
      this.swipe?.classList.add('is-dragging');
      try {
        this.swipeHandle?.setPointerCapture?.(event.pointerId);
      } catch {
        // The drag remains safe if pointer capture is unavailable.
      }
    }

    onSwipePointerMove(event) {
      if (event.pointerId !== this.swipePointerId) return;
      event.preventDefault();
      const travel = this.swipeMaxTravel();
      if (!travel) return;
      const delta = event.clientX - this.swipeStartX;
      this.setSwipeProgress(this.swipeStartProgress + delta / travel);
    }

    onSwipePointerEnd(event) {
      if (event.pointerId !== this.swipePointerId) return;
      event.preventDefault();
      this.swipe?.classList.remove('is-dragging');
      try {
        this.swipeHandle?.releasePointerCapture?.(event.pointerId);
      } catch {
        // Pointer capture may already have been released by the browser.
      }
      this.swipePointerId = null;
      if (this.swipeProgress >= SWIPE_COMPLETION_THRESHOLD) {
        this.completeSwipe();
      } else {
        this.resetSwipe();
      }
    }

    onSwipePointerCancel(event) {
      if (event.pointerId !== this.swipePointerId) return;
      this.swipe?.classList.remove('is-dragging');
      this.swipePointerId = null;
      this.resetSwipe();
    }

    onSwipeKeydown(event) {
      if (this.submitting || this.swipeComplete) {
        event.preventDefault();
        return true;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.markSwipeInteraction();
        this.completeSwipe();
        return true;
      }
      if (event.key === 'End') {
        event.preventDefault();
        this.markSwipeInteraction();
        this.completeSwipe();
        return true;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        this.resetSwipe();
        return true;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.markSwipeInteraction();
        const progress = Math.min(1, this.swipeProgress + 0.25);
        this.setSwipeProgress(progress);
        if (progress >= SWIPE_COMPLETION_THRESHOLD) this.completeSwipe();
        return true;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.markSwipeInteraction();
        this.setSwipeProgress(this.swipeProgress - 0.25);
        return true;
      }
      return false;
    }

    normalizePhone(value) {
      let phone = String(value || '').replace(/[\s().-]/g, '');
      if (phone.startsWith('+212')) phone = `0${phone.slice(4)}`;
      if (phone.startsWith('00212')) phone = `0${phone.slice(5)}`;
      return /^0[67]\d{8}$/.test(phone) ? phone : null;
    }

    validateForm() {
      const formData = new FormData(this.form);
      const fullName = this.safeText(formData.get('fullName'), 200);
      const phone = this.normalizePhone(formData.get('phone'));
      const city = this.safeText(formData.get('city'), 120);
      const address = this.safeText(formData.get('address'), 300);
      const honeypot = this.safeText(formData.get('website'), 200);
      const errors = {};

      if (fullName.length < 3 || fullName.length > 80) {
        errors.fullName = 'دخل الاسم الكامل من 3 حتى 80 حرف.';
      }
      if (!phone) {
        errors.phone = 'دخل رقم مغربي صحيح كيبدا بـ 06 أو 07.';
      }
      if (city.length < 2 || city.length > 60) {
        errors.city = 'دخل اسم المدينة من 2 حتى 60 حرف.';
      }
      if (address.length < 5 || address.length > 180) {
        errors.address = 'دخل عنوان واضح من 5 حتى 180 حرف.';
      }
      if (honeypot) {
        errors.form = 'تعذر إرسال الطلب. حاول مرة أخرى.';
      }

      ['fullName', 'phone', 'city', 'address'].forEach((name) => {
        this.setFieldError(name, errors[name] || '');
      });
      if (errors.form) this.showFormStatus(errors.form);

      return {
        valid: Object.keys(errors).length === 0,
        values: { fullName, phone, city, address, website: honeypot },
      };
    }

    setFieldError(name, message) {
      const field = this.form?.elements.namedItem(name);
      const error = this.querySelector(`[data-luma-cod-error="${name}"]`);
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement
      ) {
        field.setAttribute('aria-invalid', String(Boolean(message)));
        const errorId = `LumaCodError-${name}`;
        if (message) {
          field.setAttribute('aria-describedby', errorId);
        } else {
          field.removeAttribute('aria-describedby');
        }
        if (error) error.id = errorId;
      }
      if (error) error.textContent = message;
    }

    endpointUrl() {
      const configured = (this.dataset.endpoint || '').trim();
      if (!configured) return null;
      try {
        const url = new URL(configured);
        const valid =
          url.protocol === 'https:' &&
          url.hostname === 'script.google.com' &&
          /^\/macros\/s\/[A-Za-z0-9_-]+\/exec$/.test(url.pathname);
        return valid ? url.href : null;
      } catch {
        return null;
      }
    }

    captureAttribution() {
      const query = new URLSearchParams(window.location.search);
      return {
        pageUrl: window.location.href.slice(0, 2000),
        utmSource: this.safeText(query.get('utm_source'), 120),
        utmMedium: this.safeText(query.get('utm_medium'), 120),
        utmCampaign: this.safeText(query.get('utm_campaign'), 180),
        referrer: String(document.referrer || '').slice(0, 2000),
      };
    }

    buildPayload(values) {
      const { offerQuantity, totalPrice, selectedProducts } = this.orderContext;
      return {
        idempotencyKey: this.idempotencyKey,
        fullName: values.fullName,
        phone: values.phone,
        city: values.city,
        address: values.address,
        website: values.website,
        offerQuantity,
        totalPrice,
        selectedProducts: selectedProducts.map((product) => ({
          productId: product.productId,
          variantId: product.variantId,
          title: product.title,
        })),
        currency: 'MAD',
        source: 'NEW LUMA SHOPIFY',
        testMode: this.testMode,
        ...this.captureAttribution(),
      };
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting || !this.orderContext) return;
      if (!this.swipeComplete) {
        this.showFormStatus(
          'كمّل التأكيد بالسحب من اليسار إلى اليمين.',
        );
        this.swipeHandle?.focus();
        return;
      }

      this.showFormStatus('');
      const validation = this.validateForm();
      if (!validation.valid) {
        this.resetSwipe();
        this.querySelector('[aria-invalid="true"]')?.focus();
        return;
      }

      const endpoint = this.endpointUrl();
      if (!endpoint) {
        this.showFormStatus(
          'خدمة تسجيل الطلب مازال ما تربطاتش. حاول من بعد أو تاصل بنا.',
        );
        this.resetSwipe();
        return;
      }

      this.setSubmitting(true);
      let submissionConfirmed = false;
      this.requestController = new AbortController();
      const timeoutId = window.setTimeout(
        () => this.requestController?.abort(),
        REQUEST_TIMEOUT_MS,
      );

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(this.buildPayload(validation.values)),
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          cache: 'no-store',
          credentials: 'omit',
          redirect: 'follow',
          signal: this.requestController.signal,
        });
        if (!response.ok) {
          throw new Error('HTTP_ERROR');
        }

        const text = await response.text();
        let result;
        try {
          result = JSON.parse(text);
        } catch {
          throw new Error('MALFORMED_RESPONSE');
        }

        if (
          result?.ok !== true ||
          typeof result.orderId !== 'string' ||
          !ORDER_ID_PATTERN.test(result.orderId)
        ) {
          if (result?.ok === false && typeof result.message === 'string') {
            throw new Error(
              `SERVER:${this.safeText(result.message, 180) || 'تعذر تسجيل الطلب.'}`,
            );
          }
          throw new Error('UNCONFIRMED_SAVE');
        }

        submissionConfirmed = true;
        this.swipe?.classList.add('is-success');
        this.setSwipeLabel('تم تأكيد طلبك ✓');
        this.showSuccess(result.orderId);
      } catch (error) {
        const isAbort = error instanceof DOMException && error.name === 'AbortError';
        const serverMessage =
          error instanceof Error && error.message.startsWith('SERVER:')
            ? error.message.slice(7)
            : '';
        this.showFormStatus(
          serverMessage ||
            (isAbort
              ? 'تأخر الاتصال. معلوماتك باقية، عاود المحاولة.'
              : navigator.onLine
                ? 'ما قدرناش نأكدوا تسجيل الطلب. عاود المحاولة.'
                : 'ما كاينش الاتصال بالإنترنت. راجع الشبكة وعاود المحاولة.'),
        );
        this.showSwipeError();
      } finally {
        window.clearTimeout(timeoutId);
        this.requestController = null;
        this.setSubmitting(false);
        if (!submissionConfirmed) this.resetSwipe();
      }
    }

    setSubmitting(isSubmitting) {
      this.submitting = isSubmitting;
      if (this.nativeSubmitButton) {
        this.nativeSubmitButton.disabled = isSubmitting;
      }
      if (this.swipeHandle) this.swipeHandle.disabled = isSubmitting;
      this.swipe?.classList.toggle('is-submitting', isSubmitting);
      if (isSubmitting) {
        this.swipe?.classList.remove('is-error');
        this.setSwipeLabel('جاري تسجيل الطلب...');
      } else if (
        !this.swipe?.classList.contains('is-error') &&
        !this.swipe?.classList.contains('is-success')
      ) {
        this.setSwipeLabel('اسحب لتأكيد الطلب');
      }
      if (this.spinner) this.spinner.hidden = !isSubmitting;
      this.setAttribute('aria-busy', String(isSubmitting));
    }

    showFormStatus(message) {
      if (!this.formStatus) return;
      this.formStatus.textContent = message;
      this.formStatus.hidden = !message;
    }

    showSwipeError() {
      if (this.swipeErrorTimer) window.clearTimeout(this.swipeErrorTimer);
      this.swipe?.classList.add('is-error');
      this.setSwipeLabel('ما تسجلش الطلب، عاود المحاولة');
      this.swipeErrorTimer = window.setTimeout(() => {
        this.swipe?.classList.remove('is-error');
        if (!this.submitting) this.setSwipeLabel('اسحب لتأكيد الطلب');
        this.swipeErrorTimer = null;
      }, 1800);
    }

    showFormView() {
      if (this.formView) this.formView.hidden = false;
      if (this.successView) this.successView.hidden = true;
      if (this.orderIdOutput) this.orderIdOutput.textContent = '';
      this.showFormStatus('');
      this.resetSwipe(true);
      this.startSwipeGuide();
    }

    showSuccess(orderId) {
      if (!this.orderContext) return;
      if (this.orderIdOutput) this.orderIdOutput.textContent = orderId;
      if (this.formView) this.formView.hidden = true;
      if (this.successView) this.successView.hidden = false;
      this.successView?.focus?.();

      const selectedProducts = this.orderContext.selectedProducts.map(
        (product) => ({ ...product }),
      );
      window.dispatchEvent(
        new CustomEvent(this.dataset.savedEvent || 'luma:order-saved', {
          detail: {
            orderId,
            offerQuantity: this.orderContext.offerQuantity,
            totalPrice: this.orderContext.totalPrice,
            currency: 'MAD',
            selectedProducts,
          },
        }),
      );
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, LumaCodPopup);
  }
})();
